import { Capacitor } from "@capacitor/core";

import { supabase } from "@/integrations/supabase/client";
import { type AuthProvider, authRedirectFor, parseAuthReturn } from "@/rbapp/authReturn";
import { isRaisedBeefHomeHost } from "@/lib/siteHost";
import type { PlanInterval } from "@/lib/sitePlan";

/**
 * Paying for CM Central inside the app — Apple (and later Google) through RevenueCat.
 *
 * Owner, 2026-10-22: "I'll lose cattle people if they have to go to a site to register and
 * pull out a card." So in the app: no sign-up before paying, Face ID, Apple's own sheet.
 * Plan: https://claude.ai/artifact/FuFygyGs57jrS1cexGSpaT · screen: mock B.
 *
 * How the pieces fit:
 *   · The silent account. A new phone is signed in anonymously (supabase.auth.signInAnonymously)
 *     so every purchase has an owner from the first tap. The database gives it no website free
 *     month (migration 20261022000002); the free month here is Apple's, once per Apple ID.
 *   · RevenueCat's app user id IS the Supabase user id, set before anything is sold, so a
 *     purchase can never be stranded on one of RevenueCat's own anonymous ids.
 *   · After a purchase or a Restore the app asks rb-app-iap to re-read the person from
 *     RevenueCat at once; the webhook does the same a moment later and on every renewal.
 *   · Signing in as someone else (a website member) → logIn with the new id. RevenueCat's
 *     "Transfer to new App User ID" restore setting carries the phone's purchase with them.
 *
 * ⚠️ NATIVE ONLY. The RevenueCat and Sign in with Apple plugins are loaded with import() on a
 * phone; the website never loads them, and barn builds leave them out entirely
 * (scripts/build-barn.mjs, APP_ONLY_PLUGINS — owner, 2026-10-22: "Keep beaver clean").
 */

/** RevenueCat's PUBLIC app keys (safe in the app by design — they ship inside the binary).
 *  iOS: the App Store app "Cattle Market Central" in RevenueCat project "CM Central", created
 *  by the owner 2026-11-02; written here so the Mac build needs no env file. An env var still
 *  overrides it. Android stays empty until the Play Store app exists in RevenueCat; the paywall
 *  then says purchases are not ready instead of failing. */
const RC_KEYS = {
  ios: (import.meta.env.VITE_RC_IOS_KEY as string | undefined) || "appl_akKqncagEJStlnDOrmYcEihUhlH",
  android: (import.meta.env.VITE_RC_ANDROID_KEY as string | undefined) ?? "",
};

/** The two products, by RevenueCat package identifier in the "default" offering (ONE PLAN,
 *  2026-10-25: the Basic pair was never created in App Store Connect and is gone). Both grant
 *  the `premium` entitlement — MAIN_APP_STORE_LISTING.md §8. The website's own list price,
 *  shown until the store's localized string loads, is src/lib/sitePlan.ts. */
export type { PlanInterval };
// ⚠️ RevenueCat's STANDARD package identifiers, not the product ids. The dashboard's
// offering wizard names packages $rc_monthly / $rc_annual (seen 2026-11-02 on the
// owner's `default` offering, display names cmc_plan_monthly / cmc_plan_annual);
// the product ids cmc_plan_monthly / cmc_plan_annual live on the PRODUCTS attached
// to those packages. The app asks the offering for a package by identifier.
export const PACKAGE_ID: Record<PlanInterval, string> = { monthly: "$rc_monthly", annual: "$rc_annual" };

export const isNativeApp = (): boolean => Capacitor.isNativePlatform();

/** Whether the app's paid screens are locked here: on raisedbeef.com, and in the phone app.
 *  ⚠️ The phone app runs at capacitor://localhost, which isRaisedBeefHomeHost() does not know —
 *  before 2026-10-22 every paid tool in the iPhone build was open to everyone. */
export const rbGated = (): boolean => isRaisedBeefHomeHost() || isNativeApp();

/** Is a store purchase possible here (a phone, with its key set)? */
export function canBuy(): boolean {
  if (!isNativeApp()) return false;
  return Capacitor.getPlatform() === "ios" ? !!RC_KEYS.ios : Capacitor.getPlatform() === "android" ? !!RC_KEYS.android : false;
}

type Purchases = typeof import("@revenuecat/purchases-capacitor").Purchases;
let rc: Promise<Purchases> | null = null;
let rcUser: string | null = null;

/** The signed-in user's id, signing the phone in silently first if nobody is. */
export async function ensureUser(): Promise<string> {
  const { data } = await supabase.auth.getSession();
  if (data.session) return data.session.user.id;
  const { data: anon, error } = await supabase.auth.signInAnonymously({
    options: { data: { audience: "rbsite", signup_source: "app" } },
  });
  if (error || !anon.user) throw new Error("We couldn't reach RaisedBeef. Check your signal and try again.");
  return anon.user.id;
}

/** RevenueCat, configured for the signed-in person. */
async function purchases(): Promise<Purchases> {
  const uid = await ensureUser();
  if (!rc) {
    rc = (async () => {
      const { Purchases } = await import("@revenuecat/purchases-capacitor");
      const apiKey = Capacitor.getPlatform() === "ios" ? RC_KEYS.ios : RC_KEYS.android;
      await Purchases.configure({ apiKey, appUserID: uid });
      rcUser = uid;
      return Purchases;
    })();
    rc.catch(() => { rc = null; });
  }
  const P = await rc;
  if (rcUser !== uid) {
    await P.logIn({ appUserID: uid });
    rcUser = uid;
  }
  return P;
}

/** Called when the person changes (sign-in, sign-out): RevenueCat follows the Supabase id. */
export async function followUser(): Promise<void> {
  if (!canBuy() || !rc) return;
  try { await purchases(); } catch { /* next purchase retries */ }
}

/** What the store says: price strings ("$29.99") by interval, null where it has not answered,
 *  and whether Apple will give THIS Apple ID the free month. ⚠️ Apple's free month is once per
 *  Apple ID: someone who used it must not be shown "Start free month" and then charged (3.1.2). */
export interface StoreOffer { prices: Record<PlanInterval, string | null>; freeMonth: boolean }

export async function storeOffer(): Promise<StoreOffer | null> {
  if (!canBuy()) return null;
  try {
    const P = await purchases();
    const offers = await P.getOfferings();
    const pkgs = offers.current?.availablePackages ?? [];
    const pkg = (id: string) => pkgs.find((p) => p.identifier === id);
    const price = (id: string) => pkg(id)?.product.priceString ?? null;
    // Eligibility is per subscription group, so one product answers for both. Unknown
    // (status 0) is treated as no free month: saying "free" to someone who is charged is the
    // worse mistake.
    let freeMonth = false;
    const probe = pkg(PACKAGE_ID.monthly)?.product;
    if (probe?.introPrice && probe.introPrice.price === 0) {
      if (Capacitor.getPlatform() === "ios") {
        const el = await P.checkTrialOrIntroductoryPriceEligibility({ productIdentifiers: [probe.identifier] });
        freeMonth = el[probe.identifier]?.status === 2;
      } else {
        freeMonth = true; // Google only offers the trial to an eligible account
      }
    }
    return { prices: { monthly: price(PACKAGE_ID.monthly), annual: price(PACKAGE_ID.annual) }, freeMonth };
  } catch {
    return null;
  }
}

export type BuyResult = { ok: true } | { ok: false; cancelled: boolean; message: string };

/** Apple's sheet for the plan, monthly or yearly. On success the database is updated before this returns. */
export async function buy(interval: PlanInterval): Promise<BuyResult> {
  if (!canBuy()) return { ok: false, cancelled: false, message: "Subscriptions open in the iPhone app." };
  try {
    const P = await purchases();
    const offers = await P.getOfferings();
    const pkg = offers.current?.availablePackages.find((p) => p.identifier === PACKAGE_ID[interval]);
    if (!pkg) return { ok: false, cancelled: false, message: "That plan isn't available right now. Try again in a minute." };
    await P.purchasePackage({ aPackage: pkg });
    await syncPlan();
    return { ok: true };
  } catch (e) {
    const err = e as { userCancelled?: boolean | null; code?: string | number; message?: string };
    if (err.userCancelled || String(err.code) === "1") return { ok: false, cancelled: true, message: "" };
    return { ok: false, cancelled: false, message: "The purchase didn't go through. You weren't charged." };
  }
}

/** Restore Purchases (Apple requires the button): finds this Apple ID's plan for this person. */
export async function restore(): Promise<{ found: boolean; message: string }> {
  if (!canBuy()) return { found: false, message: "Restore works in the iPhone app." };
  try {
    const P = await purchases();
    const { customerInfo } = await P.restorePurchases();
    await syncPlan();
    const found = Object.keys(customerInfo.entitlements.active ?? {}).length > 0;
    return { found, message: found ? "Your plan is back." : "No plan found for this Apple ID." };
  } catch {
    return { found: false, message: "Couldn't restore just now. Try again in a minute." };
  }
}

/** Ask the server to re-read this person's plan from RevenueCat now (rb-app-iap). */
export async function syncPlan(): Promise<void> {
  try { await supabase.functions.invoke("rb-app-iap", { body: { sync: true } }); } catch { /* the webhook follows */ }
  window.dispatchEvent(new Event(ACCESS_CHANGED));
}

/** Fired after a purchase or Restore; useSiteAccess re-reads the plan on it. */
export const ACCESS_CHANGED = "rb-access-changed";

/** Is the signed-in person the phone's silent account (no email, no Apple sign-in yet)? */
export async function isSilentAccount(): Promise<boolean> {
  const { data } = await supabase.auth.getSession();
  return !!(data.session?.user as { is_anonymous?: boolean } | undefined)?.is_anonymous;
}

/** Apple's page for changing or cancelling a subscription. */
export const MANAGE_SUBSCRIPTIONS_URL = "https://apps.apple.com/account/subscriptions";

/**
 * "Save your account" — Sign in with Apple, linked to the silent account so the id (and so the
 * purchase) never changes. Needs the Apple provider on in Supabase with com.raisedbeef.app as a
 * client id, and the Sign in with Apple capability in Xcode.
 */
export async function saveWithApple(): Promise<{ ok: boolean; message: string }> {
  if (!isNativeApp() || Capacitor.getPlatform() !== "ios") return { ok: false, message: "Sign in with Apple works in the iPhone app." };
  try {
    const raw = randomNonce();
    const hashed = await sha256Hex(raw);
    const { SignInWithApple } = await import("@capacitor-community/apple-sign-in");
    const res = await SignInWithApple.authorize({
      clientId: "com.raisedbeef.app",
      redirectURI: "https://raisedbeef.com/m",
      scopes: "email name",
      nonce: hashed,
    });
    const token = res.response?.identityToken;
    if (!token) return { ok: false, message: "Apple didn't send a sign-in. Try again." };
    const { error } = await supabase.auth.linkIdentity({ provider: "apple", token, nonce: raw });
    if (error) {
      // This Apple ID already belongs to an account: sign in to that one instead. The phone's
      // purchase moves with the person (RevenueCat transfer on logIn).
      const { error: e2 } = await supabase.auth.signInWithIdToken({ provider: "apple", token, nonce: raw });
      if (e2) return { ok: false, message: "Couldn't save your account. Try again in a minute." };
    }
    await followUser();
    await syncPlan();
    return { ok: true, message: "Saved. Sign in with Apple on any phone. For a computer, set a password in You." };
  } catch (e) {
    const code = String((e as { code?: string | number }).code ?? "");
    if (code === "1001" || /cancel/i.test(String((e as Error).message))) return { ok: false, message: "" };
    return { ok: false, message: "Couldn't save your account. Try again in a minute." };
  }
}

/** The phone's OS, for choosing the sign-in doors to draw. */
export const platformName = (): string => Capacitor.getPlatform();

/** Fired when a browser-sheet sign-in comes back on the deep link: `{ ok, message }`. */
export const AUTH_RETURNED = "rb-auth-returned";

/**
 * "Save your account" with Google, or with Apple where there is no native Apple
 * sheet (Android). Supabase's OAuth flow in the phone's browser sheet, back to
 * the app on com.raisedbeef.app://auth (src/rbapp/authReturn.ts). On a silent
 * account the identity is LINKED so the id — and the purchase — never changes;
 * when that identity already belongs to someone, handleAuthDeepLink signs in to
 * that account instead. Needs the provider on in Supabase, the deep link in its
 * redirect list, and "Allow manual linking" on (launch checklist D9).
 *
 * `pending: true` means the sheet is open and the answer arrives on AUTH_RETURNED.
 */
export async function saveWithProvider(provider: AuthProvider): Promise<{ ok: boolean; message: string; pending?: boolean }> {
  if (!isNativeApp()) return { ok: false, message: "This works in the phone app." };
  if (provider === "apple" && Capacitor.getPlatform() === "ios") return saveWithApple();
  return startBrowserSignIn(provider, "link");
}

async function startBrowserSignIn(provider: AuthProvider, mode: "link" | "signin"): Promise<{ ok: boolean; message: string; pending?: boolean }> {
  try {
    const options = { redirectTo: authRedirectFor(provider), skipBrowserRedirect: true };
    const link = mode === "link" && (await isSilentAccount());
    const r = link ? await supabase.auth.linkIdentity({ provider, options }) : await supabase.auth.signInWithOAuth({ provider, options });
    const url = r.data?.url;
    if (r.error || !url) return { ok: false, message: "Couldn't start the sign-in. Try again in a minute." };
    const { Browser } = await import("@capacitor/browser");
    await Browser.open({ url, presentationStyle: "popover" });
    return { ok: true, pending: true, message: "" };
  } catch {
    return { ok: false, message: "Couldn't start the sign-in. Try again in a minute." };
  }
}

/** The deep link handler (src/lib/nativeBootstrap.ts). true when the address was ours. */
export async function handleAuthDeepLink(url: string): Promise<boolean> {
  const r = parseAuthReturn(url);
  if (!r) return false;
  try {
    const { Browser } = await import("@capacitor/browser");
    await Browser.close();
  } catch {
    /* the sheet may already be gone */
  }
  let ok = false;
  let message = "";
  if (r.kind === "error") {
    // The Google / Apple account already belongs to another member: sign in to THAT one.
    // The phone's purchase moves with the person (RevenueCat transfer on logIn).
    if (r.provider && /identity_already_exists|already/i.test(`${r.code} ${r.description}`)) {
      const again = await startBrowserSignIn(r.provider, "signin");
      if (again.ok) return true;
    }
    message = "Couldn't save your account. Try again in a minute.";
  } else {
    const res = r.kind === "code"
      ? await supabase.auth.exchangeCodeForSession(r.code)
      : await supabase.auth.setSession({ access_token: r.access_token, refresh_token: r.refresh_token });
    if (res.error) message = "Couldn't finish the sign-in. Try again in a minute.";
    else {
      ok = true;
      await followUser();
      await syncPlan();
      message = "Saved. Sign in the same way on any phone, or on raisedbeef.com.";
    }
  }
  window.dispatchEvent(new CustomEvent(AUTH_RETURNED, { detail: { ok, message } }));
  if (ok) window.dispatchEvent(new Event(ACCESS_CHANGED));
  return true;
}

function randomNonce(): string {
  const b = new Uint8Array(24);
  crypto.getRandomValues(b);
  return Array.from(b, (x) => x.toString(16).padStart(2, "0")).join("");
}

async function sha256Hex(s: string): Promise<string> {
  const d = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(s));
  return Array.from(new Uint8Array(d), (x) => x.toString(16).padStart(2, "0")).join("");
}
